/**
 * @module FilterService
 *
 * Filter state management for the Cases page.
 *
 * ## Pure functions (fully testable)
 * - {@link computeDefaultMonthYear} — previous-month default with January edge case
 * - {@link resolveFiltersQuery} — merges URL params, cached filters, and defaults
 * - {@link groupToTree} — converts flat AgencyGroup to a PrimeReact TreeNode
 * - {@link getInitialCaseFilter} — derives TEAM/USER/AGENCY scope from profile
 * - {@link getDefaultScope} — resolves scope with impersonation override
 * - {@link mapToNumberArray} — parses comma-separated query param values
 *
 * ## Orchestrator
 * - {@link setUpInitialLoad} — async; fetches groups, resolves filter state,
 *   calls `navigate()` to apply the resolved URL. Has side effects.
 *
 * @example
 * const { month, year } = computeDefaultMonthYear(new Date('2026-01-15'));
 * // { month: '11', year: '2025' }  — January rolls back to December
 */
import { Dispatch } from 'react';
import { NavigateFunction, Location } from 'react-router';
import { parseQuery, stringifyQuery, ParsedQuery } from '../utils/queryParams';
import { AgencyGroup, getGroups } from '../repositories/agencyGroupRepository';
import {
  ICasesPageDataState,
  ScopeOption,
} from '../repositories/caseRepository';
import { ImpersonationRecord, Profile } from '../shared/types/appTypes';
import { storage } from '../services/StorageService';

export interface FiltersQueryState {
  scope: ScopeOption;
  groupIds?: number[];
  caseworkerIds?: number[];
  sort?: 'datetime' | 'importance';
  factor?: string;
}

export interface FiltersQueryStateWithPagination extends FiltersQueryState {
  page: number;
  month?: string;
  year?: string;
}

export interface TreeNode {
  key: string | number;
  label: string;
  data?: unknown;
  icon?: string;
  children?: TreeNode[];
}

export const mapToNumberArray = (arg: string | string[]) =>
  Array.isArray(arg) ? arg.map((gid) => Number(gid)) : [Number(arg)];

export const groupToTree = (group: AgencyGroup): TreeNode => ({
  label: group.name || 'Unnamed',
  key: group.id,
  children: group.subGroups?.length
    ? group.subGroups
        .map(groupToTree)
        .sort((a, b) => a.label.localeCompare(b.label))
    : [],
});

export const getInitialCaseFilter = (
  profile: Profile | ImpersonationRecord,
) => {
  if (
    Number(profile.direct_report_count) > 0 &&
    profile.direct_report_cases_count != null &&
    Number(profile.direct_report_cases_count) > 0
  ) {
    return 'TEAM' as ScopeOption;
  }
  if (Number(profile.cases_count) > 0) {
    return 'USER' as ScopeOption;
  }
  return 'AGENCY' as ScopeOption;
};

export const getDefaultScope = (
  profile: Profile | null,
  impersonation: ImpersonationRecord | null,
) => {
  if (impersonation) {
    return getInitialCaseFilter(impersonation);
  }
  if (profile) {
    return getInitialCaseFilter(profile);
  }
  return 'AGENCY' as ScopeOption;
};

/**
 * Returns the default { month, year } for the cases filter, defaulting to
 * the previous calendar month.  Handles the January edge case where the
 * previous month is December of the prior year.
 *
 * @param now - injectable for testing; defaults to `new Date()`
 */
export const computeDefaultMonthYear = (
  now: Date = new Date(),
): { month: string; year: string } => {
  const isJanuary = now.getMonth() - 1 === -1;
  return {
    month: `${isJanuary ? 11 : now.getMonth() - 1}`,
    year: `${isJanuary ? now.getFullYear() - 1 : now.getFullYear()}`,
  };
};

/**
 * Resolves the filter query to navigate to on initial load.
 *
 * When a scope is already present in the URL it is used as-is and this
 * function returns null (no navigation needed).  Otherwise it merges cached
 * filters or falls back to the user's default scope.
 *
 * @param existingQuery  - parsed query string from the current URL
 * @param cachedFilters  - filters stored in localStorage (null if absent)
 * @param defaults       - { scope, page, sort, month, year } derived from
 *                         profile and route context
 * @returns merged query object to navigate to, or null if already resolved
 */
export const resolveFiltersQuery = (
  existingQuery: ParsedQuery,
  cachedFilters: (FiltersQueryState & { sort?: string }) | null,
  defaults: {
    scope: ScopeOption;
    page: number;
    sort: string | null | (string | null)[];
    month: string;
    year: string;
    queryId?: string;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> | null => {
  if (existingQuery.scope) {
    return null; // scope already in URL — nothing to do
  }

  if (cachedFilters) {
    const filters = { ...cachedFilters };
    if (filters.sort && !defaults.sort) {
      filters.sort = undefined;
    }
    return {
      month: defaults.queryId ? defaults.month : undefined,
      year: defaults.queryId ? defaults.year : undefined,
      ...existingQuery,
      ...filters,
      page: 1,
    };
  }

  return {
    ...existingQuery,
    scope: defaults.scope,
    page: defaults.page,
    sort: defaults.sort === null ? undefined : defaults.sort,
    month: defaults.queryId ? defaults.month : undefined,
    year: defaults.queryId ? defaults.year : undefined,
  };
};

export async function setUpInitialLoad(
  profile: Profile,
  impersonation: ImpersonationRecord | null,
  navigate: NavigateFunction,
  location: Location,
  sortParam: string | null,
  dataDispatch: Dispatch<Partial<ICasesPageDataState>>,
  queryId?: string,
) {
  const existingQuery = parseQuery(location.search);
  const [groups] = await Promise.all([getGroups(queryId)]);
  dataDispatch({
    groupData: groups?.map((group) => groupToTree(group)).flat(),
  });

  const page = Number(existingQuery.page || '1');
  const sort = existingQuery.sort || sortParam;
  const { month, year } = computeDefaultMonthYear();
  const resolvedMonth = (existingQuery.month as string | undefined) ?? month;
  const resolvedYear = (existingQuery.year as string | undefined) ?? year;

  const cachedFilters = storage.getFilters()
    ? (JSON.parse(storage.getFilters() as string) as FiltersQueryState)
    : null;

  const query = resolveFiltersQuery(existingQuery, cachedFilters, {
    scope: getDefaultScope(profile, impersonation),
    page,
    sort,
    month: resolvedMonth,
    year: resolvedYear,
    queryId,
  });

  if (query) {
    void navigate(
      {
        pathname: location.pathname,
        search: stringifyQuery(query),
      },
      { replace: true },
    );
  }
}
